import { MapPin, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import EthnicityMultiSelect from '@/components/EthnicityMultiSelect';
import { toEthnicityArray, hasIndigenous } from '@/utils/ethnicity';

const INDIGENOUS = ['Native American', 'Alaska Native', 'First Nations', 'Métis', 'Inuit', 'Native Hawaiian'];

/**
 * Step 1 — Business Info.
 * Ethnicity + tribal affiliation, owner name, optional business name,
 * category selection with custom-service suggestion, and location with
 * OSM autocomplete.
 */
export default function Step1BusinessInfo({
  ethnicity,
  setEthnicity,
  tribalAffiliation,
  setTribalAffiliation,
  ownerName,
  setOwnerName,
  businessName,
  setBusinessName,
  age,
  setAge,
  gender,
  setGender,
  categories,
  selectedCategories,
  toggleCategory,
  mainCategory,
  setMainCategory,
  showCustomService,
  setShowCustomService,
  customServiceName,
  setCustomServiceName,
  submittingCustom,
  submitCustomService,
  location,
  setLocation,
  locationVerified,
  setLocationVerified,
  locationSuggestions,
  setLocationSuggestions,
  fetchLocationSuggestions,
}) {
  return (
    <div className="space-y-6">
      {/* Ethnic Background — hidden if already chosen at signup to avoid re-asking */}
      {!(ethnicity && ethnicity.length > 0) && (
        <div>
          <Label className="text-stone-300 font-outfit font-bold">
            Ethnic Background * <span className="text-stone-500 text-xs font-normal">(select all that apply)</span>
          </Label>
          <div className="mt-2">
            <EthnicityMultiSelect
              value={ethnicity}
              onChange={(next) => {
                setEthnicity(next);
                if (!next.some((e) => INDIGENOUS.includes(e))) setTribalAffiliation('');
              }}
              testId="onboard-ethnicity"
            />
          </div>
          <p className="text-stone-500 text-xs mt-2 font-outfit">Required. Shown on your public profile. Pick every background that represents you.</p>
        </div>
      )}

      {hasIndigenous(ethnicity) && (
        <div>
          <Label className="text-stone-300 font-outfit font-bold">
            Tribal Affiliation <span className="text-stone-500 text-sm">(Recommended)</span>
          </Label>
          <Input
            value={tribalAffiliation}
            onChange={(e) => setTribalAffiliation(e.target.value)}
            placeholder="e.g., Navajo Nation, Cherokee Nation, Cree Nation…"
            className="input-field w-full mt-2"
            data-testid="tribal-affiliation-input"
          />
          <p className="text-stone-500 text-xs mt-1 font-outfit">
            Your specific tribe or nation. Optional but recommended for Indigenous vendors.
          </p>
        </div>
      )}

      <div>
        <Label className="text-stone-300 font-outfit font-bold">Owner Name *</Label>
        <Input
          value={ownerName}
          onChange={(e) => setOwnerName(e.target.value)}
          placeholder="Your full name"
          className="input-field w-full mt-2"
          data-testid="owner-name"
          required
        />
      </div>

      <div>
        <Label className="text-stone-300 font-outfit">Business Name (Optional)</Label>
        <Input
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          placeholder="Your business name (optional)"
          className="input-field w-full mt-2"
          data-testid="business-name"
        />
        <p className="text-stone-500 text-xs mt-1 font-outfit">
          Leave blank if you operate under your own name.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {!age && (
          <div>
            <Label className="text-stone-300 font-outfit font-bold">Age *</Label>
            <Input
              type="number"
              min={13}
              max={120}
              value={age}
              onChange={(e) => setAge(e.target.value)}
              placeholder="e.g., 28"
              className="input-field w-full mt-2"
              data-testid="age-input"
              required
            />
            <p className="text-stone-500 text-xs mt-1 font-outfit">Private — only Master reviewers can see this.</p>
          </div>
        )}
        <div className={age ? 'col-span-2' : ''}>
          <Label className="text-stone-300 font-outfit font-bold">Gender *</Label>
          <select
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            className="input-field w-full mt-2 bg-stone-900 border border-stone-700 rounded-xl text-white p-3"
            data-testid="gender-select"
            required
          >
            <option value="">Select…</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="non_binary">Non-Binary</option>
          </select>
          <p className="text-stone-500 text-xs mt-1 font-outfit">Displayed publicly on your vendor card.</p>
        </div>
      </div>

      <div>
        <Label className="text-stone-300 font-outfit mb-3 block">
          Select Your Services * <span className="text-stone-500 text-sm">(Select all that apply)</span>
        </Label>
        <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto p-2 bg-stone-900/50 rounded-xl" data-testid="category-selection">
          {categories.map((cat) => (
            <button
              key={cat.category_id}
              type="button"
              onClick={() => toggleCategory(cat.category_id)}
              className={`px-4 py-2 rounded-full text-sm font-outfit transition-all duration-200 ${
                selectedCategories.includes(cat.category_id)
                  ? 'bg-yellow-400 text-black font-bold shadow-lg shadow-yellow-400/20'
                  : 'bg-stone-800 text-stone-300 hover:bg-stone-700'
              }`}
              data-testid={`category-${cat.category_id}`}
            >
              {cat.name}
              {selectedCategories.includes(cat.category_id) && <X className="w-3 h-3 ml-1 inline" />}
            </button>
          ))}
        </div>
        {selectedCategories.length > 0 && (
          <p className="text-yellow-400 text-sm mt-2 font-outfit">
            {selectedCategories.length} service{selectedCategories.length > 1 ? 's' : ''} selected
          </p>
        )}

        {selectedCategories.length > 0 && (
          <div className="mt-4">
            <Label className="text-stone-300 font-outfit mb-2 block">
              Main Category * <span className="text-stone-500 text-sm">(Shown on your vendor card)</span>
            </Label>
            <select
              value={mainCategory}
              onChange={(e) => setMainCategory(e.target.value)}
              className="w-full bg-stone-900 border border-stone-700 rounded-xl text-white text-sm p-3"
              data-testid="main-category-select"
            >
              <option value="">Choose your primary category...</option>
              {categories.filter((c) => selectedCategories.includes(c.category_id)).map((cat) => (
                <option key={cat.category_id} value={cat.category_id}>{cat.name}</option>
              ))}
            </select>
            <p className="text-stone-500 text-xs mt-1 font-outfit">This will be displayed prominently on your vendor card</p>
          </div>
        )}

        {!showCustomService ? (
          <button
            type="button"
            onClick={() => setShowCustomService(true)}
            className="mt-3 text-sm text-[#A78BFA] hover:text-white transition-colors font-outfit underline underline-offset-4"
            data-testid="add-custom-service-btn"
          >
            Don't see your services listed? Add it now
          </button>
        ) : (
          <div className="mt-3 p-4 rounded-xl border border-[#8B5CF6]/30 bg-[#8B5CF6]/5 space-y-3">
            <Label className="text-stone-300 font-outfit text-sm">Describe your service</Label>
            <Input
              value={customServiceName}
              onChange={(e) => setCustomServiceName(e.target.value)}
              placeholder="e.g., Beadwork, Traditional Healing, etc."
              className="input-field w-full"
              maxLength={60}
              data-testid="custom-service-input"
            />
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={submitCustomService}
                disabled={!customServiceName.trim() || submittingCustom}
                className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm"
                data-testid="submit-custom-service"
              >
                {submittingCustom ? 'Adding...' : 'Add Service'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setShowCustomService(false)} className="text-stone-500 text-sm">Cancel</Button>
            </div>
          </div>
        )}
      </div>

      <div>
        <Label className="text-stone-300 font-outfit">Location (City, State only) *</Label>
        <div className="relative mt-2">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500 z-10" />
          <Input
            value={location}
            onChange={(e) => {
              setLocation(e.target.value);
              setLocationVerified(false);
              if (e.target.value.length > 2) fetchLocationSuggestions(e.target.value);
              else setLocationSuggestions([]);
            }}
            placeholder="e.g. Phoenix, Arizona"
            className="input-field w-full pl-10"
            data-testid="location-input"
          />
          {locationVerified && <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-400" />}
        </div>
        <p className="text-stone-400/70 text-xs mt-1">For your privacy, we only collect city & state — never a street address.</p>
        {locationSuggestions.length > 0 && (
          <div className="mt-1 bg-stone-800 rounded-xl border border-stone-700 overflow-hidden max-h-40 overflow-y-auto" data-testid="location-suggestions">
            {locationSuggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  setLocation(s.display);
                  setLocationVerified(true);
                  setLocationSuggestions([]);
                }}
                className="w-full text-left px-4 py-2.5 text-sm text-stone-300 hover:bg-stone-700 transition-colors flex items-center gap-2"
              >
                <MapPin className="w-3.5 h-3.5 text-stone-500 flex-shrink-0" />
                <span className="truncate">{s.display}</span>
              </button>
            ))}
          </div>
        )}
        {location && !locationVerified && locationSuggestions.length === 0 && (
          <p className="text-yellow-400/70 text-xs mt-1">Please select a location from the suggestions to verify it</p>
        )}
      </div>
    </div>
  );
}
